import { useEffect, useState, type RefObject } from 'react'

/**
 * useElementWidth — the header overflow menu's one measurement seam
 * (Asana 1217649894859355 — dynamic formatting in the top menu).
 *
 * Returns the observed width of `ref`'s element, or `undefined` while it has
 * never been measured. `undefined` is a real state that callers act on, not a
 * placeholder: `computeFit` reads it as "not measured yet, show everything",
 * which is what keeps every facet tab in the strip until a width is known.
 *
 * Two rules this hook keeps, both of which have bitten before:
 *
 * 1. **`ResizeObserver` only — no `getBoundingClientRect` fallback.** jsdom has
 *    no `ResizeObserver`, so a fallback would report 0 in every test file that
 *    does not stub one, and `computeFit` treats a measured 0 as "genuinely no
 *    room" and hides the inactive facets. That would break
 *    `fleetEditorNewAgentArea.contract.test.tsx:183`, which asserts the
 *    *inactive* 'Persona' tab is present in the header slot. Staying
 *    `undefined` keeps "unmeasured means show everything" true everywhere.
 * 2. **The constructor is guarded on `typeof ResizeObserver === 'undefined'`.**
 *    That guard is an implementation requirement, not a test convenience:
 *    `OverflowNav` mounts in test files that declare no stub at all
 *    (`fleetEditorNewAgentArea`, `entityDeepLinks`), and an unguarded
 *    `new ResizeObserver(...)` is a `ReferenceError` there, not a zero.
 *
 * A measured 0 stays 0 — it is a real answer, distinct from `undefined`.
 *
 * **Limit, for a new caller:** the effect keys on the ref OBJECT, so it observes
 * whatever `ref.current` holds at mount. An element attached later — a
 * conditionally rendered node, a ref reassigned after a state change — is never
 * observed, and the width stays `undefined` forever. Today's callers attach the
 * ref on their first render, which is the only shape this supports.
 */
export function useElementWidth(ref: RefObject<Element | null>): number | undefined {
  const [width, setWidth] = useState<number | undefined>(undefined)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    // No fallback measurement here: see rule 1 above. Without an observer the
    // width stays `undefined`, which every caller already handles.
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(entries => {
      const last = entries[entries.length - 1]
      if (last) setWidth(last.contentRect.width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref])

  return width
}
